import React, { useEffect } from 'react';
import { View, Text, StyleSheet, Platform, Linking, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import { useViewportWidth } from '../contexts/AppLayoutContext';
import {
  LEGAL_CONTACT_EMAIL,
  LEGAL_EFFECTIVE_DATE,
  PRIVACY_POLICY,
  TERMS_OF_USE,
} from '../content/legalContent';

const WIDE_BREAKPOINT = 960;
const ORANGE = '#e67e22';

function palette(isDarkMode) {
  return isDarkMode
    ? { page: '#121212', ink: '#f4efe6', soft: 'rgba(244,239,230,0.72)', faint: 'rgba(244,239,230,0.14)', accent: '#f39c4a' }
    : { page: '#f7f3ec', ink: '#1a1a1a', soft: 'rgba(26,26,26,0.7)', faint: 'rgba(26,26,26,0.12)', accent: '#b8560f' };
}

// Turns the contact address inside a paragraph into a mailto link.
function RichText({ text, style, linkStyle }) {
  const parts = String(text).split(LEGAL_CONTACT_EMAIL);
  return (
    <Text style={style}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 ? (
            <Text
              style={linkStyle}
              accessibilityRole="link"
              onPress={() => Linking.openURL(`mailto:${LEGAL_CONTACT_EMAIL}`)}
            >
              {LEGAL_CONTACT_EMAIL}
            </Text>
          ) : null}
        </React.Fragment>
      ))}
    </Text>
  );
}

function scrollToSection(id, instant = false) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const el = document.getElementById(`legal-${id}`);
  if (!el) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce || instant ? 'auto' : 'smooth', block: 'start' });
}

export function LegalPage({ doc }) {
  const navigation = useNavigation();
  const { isDarkMode } = useTheme();
  const width = useViewportWidth();
  const wide = width >= WIDE_BREAKPOINT;
  const c = palette(isDarkMode);
  const other = doc.id === 'privacy' ? TERMS_OF_USE : PRIVACY_POLICY;

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const id = decodeURIComponent(window.location.hash.replace(/^#(legal-)?/, ''));
    if (!id || !doc.sections.some((s) => s.id === id)) return undefined;
    // The app shell resets scroll while it mounts, so jump again as layout settles.
    const timers = [300, 900, 1800].map((ms) => setTimeout(() => scrollToSection(id, true), ms));
    return () => timers.forEach(clearTimeout);
  }, [doc]);

  const body = { color: c.soft };
  const link = { color: c.accent, textDecorationLine: 'underline' };

  return (
    <View style={[styles.page, { backgroundColor: c.page }]}>
      <View style={[styles.hero, wide && styles.heroWide]}>
        <Text style={[styles.eyebrow, { color: c.accent }]}>{doc.eyebrow}</Text>
        <Text accessibilityRole="header" aria-level={1} style={[styles.title, wide && styles.titleWide, { color: c.ink }]}>
          {doc.title}
        </Text>
        <View style={[styles.rule, { backgroundColor: ORANGE }]} />
        <Text style={[styles.meta, { color: c.soft }]}>Effective {LEGAL_EFFECTIVE_DATE}</Text>
        <Text style={[styles.intro, wide && styles.introWide, { color: c.ink }]}>{doc.intro}</Text>
      </View>

      <View style={[styles.columns, wide && styles.columnsWide]}>
        <View
          style={[styles.toc, wide && styles.tocWide, { borderColor: c.faint }]}
          accessibilityRole="navigation"
          aria-label="On this page"
        >
          <Text style={[styles.tocLabel, { color: c.soft }]}>On this page</Text>
          {doc.sections.map((s, i) => (
            <Pressable
              key={s.id}
              accessibilityRole="link"
              onPress={() => scrollToSection(s.id)}
              style={({ hovered }) => [styles.tocItem, hovered && { opacity: 0.7 }]}
            >
              <Text style={[styles.tocNum, { color: c.accent }]}>{String(i + 1).padStart(2, '0')}</Text>
              <Text style={[styles.tocText, { color: c.ink }]}>{s.heading}</Text>
            </Pressable>
          ))}
        </View>

        <View style={[styles.article, wide && styles.articleWide]}>
          {doc.sections.map((s, i) => (
            <View key={s.id} nativeID={`legal-${s.id}`} style={[styles.section, { borderTopColor: c.faint }]}>
              <View style={styles.sectionHead}>
                <Text style={[styles.sectionNum, { color: c.accent }]}>{String(i + 1).padStart(2, '0')}</Text>
                <Text accessibilityRole="header" aria-level={2} style={[styles.sectionTitle, { color: c.ink }]}>
                  {s.heading}
                </Text>
              </View>
              {(s.paragraphs || []).map((p, j) => (
                <RichText key={`p${j}`} text={p} style={[styles.paragraph, body]} linkStyle={link} />
              ))}
              {s.bullets ? (
                <View style={styles.list}>
                  {s.bullets.map((b, j) => (
                    <View key={`b${j}`} style={styles.listItem}>
                      <View style={[styles.dot, { backgroundColor: ORANGE }]} />
                      <RichText text={b} style={[styles.paragraph, styles.listText, body]} linkStyle={link} />
                    </View>
                  ))}
                </View>
              ) : null}
              {(s.paragraphsAfter || []).map((p, j) => (
                <RichText key={`a${j}`} text={p} style={[styles.paragraph, body]} linkStyle={link} />
              ))}
            </View>
          ))}

          <Pressable
            accessibilityRole="link"
            onPress={() => navigation.navigate(other.id === 'privacy' ? 'Privacy' : 'Terms')}
            style={({ hovered }) => [styles.next, { borderColor: c.faint }, hovered && { borderColor: ORANGE }]}
          >
            <Text style={[styles.nextLabel, { color: c.soft }]}>Also read</Text>
            <Text style={[styles.nextTitle, { color: c.ink }]}>{other.title} →</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

export function PrivacyPolicy() {
  return <LegalPage doc={PRIVACY_POLICY} />;
}

export function TermsOfUse() {
  return <LegalPage doc={TERMS_OF_USE} />;
}

const styles = StyleSheet.create({
  page: { flex: 1, width: '100%', paddingHorizontal: 20, paddingTop: 32, paddingBottom: 72 },
  hero: { width: '100%', maxWidth: 1120, alignSelf: 'center', paddingBottom: 32 },
  heroWide: { paddingTop: 40, paddingBottom: 56 },
  eyebrow: { fontFamily: 'VastagoGrotesk', fontSize: 13, letterSpacing: 2.4, textTransform: 'uppercase', marginBottom: 12 },
  title: { fontFamily: 'Playpen Sans', fontSize: 44, lineHeight: 50, fontWeight: '700' },
  titleWide: { fontSize: 80, lineHeight: 86, letterSpacing: -1.5 },
  rule: { width: 72, height: 4, borderRadius: 2, marginTop: 20, marginBottom: 16 },
  meta: { fontFamily: 'VastagoGrotesk', fontSize: 14, marginBottom: 20 },
  intro: { fontFamily: 'VastagoGrotesk', fontSize: 18, lineHeight: 28, maxWidth: 720 },
  introWide: { fontSize: 21, lineHeight: 33 },
  columns: { width: '100%', maxWidth: 1120, alignSelf: 'center' },
  columnsWide: { flexDirection: 'row', alignItems: 'flex-start', gap: 64 },
  toc: { borderTopWidth: 1, borderBottomWidth: 1, paddingVertical: 16, marginBottom: 16 },
  tocWide: Platform.select({
    web: { width: 260, flexShrink: 0, position: 'sticky', top: 96, borderBottomWidth: 0, marginBottom: 0 },
    default: { width: 260, flexShrink: 0 },
  }),
  tocLabel: { fontFamily: 'VastagoGrotesk', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 },
  tocItem: { flexDirection: 'row', alignItems: 'baseline', gap: 10, paddingVertical: 6, minHeight: 32 },
  tocNum: { fontFamily: 'VastagoGrotesk', fontSize: 12, fontVariant: ['tabular-nums'] },
  tocText: { fontFamily: 'VastagoGrotesk', fontSize: 15, lineHeight: 20, flexShrink: 1 },
  article: { flex: 1, minWidth: 0, maxWidth: 720 },
  articleWide: {},
  section: Platform.select({
    web: { borderTopWidth: 1, paddingTop: 28, paddingBottom: 12, scrollMarginTop: 96 },
    default: { borderTopWidth: 1, paddingTop: 28, paddingBottom: 12 },
  }),
  sectionHead: { flexDirection: 'row', alignItems: 'baseline', gap: 14, marginBottom: 14 },
  sectionNum: { fontFamily: 'VastagoGrotesk', fontSize: 14, fontVariant: ['tabular-nums'] },
  sectionTitle: { fontFamily: 'Playpen Sans', fontSize: 26, lineHeight: 32, fontWeight: '600', flexShrink: 1 },
  paragraph: { fontFamily: 'VastagoGrotesk', fontSize: 17, lineHeight: 28, marginBottom: 16 },
  list: { marginBottom: 8 },
  listItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 14 },
  dot: { width: 6, height: 6, borderRadius: 3, marginTop: 12 },
  listText: { flex: 1, marginBottom: 12 },
  next: { marginTop: 40, borderWidth: 1, borderRadius: 14, padding: 24 },
  nextLabel: { fontFamily: 'VastagoGrotesk', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 },
  nextTitle: { fontFamily: 'Playpen Sans', fontSize: 26, fontWeight: '600' },
});
